import { Link, useLocation } from '@tanstack/react-router';
import { Waves, LogOut, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { useSignOut } from '@/hooks/useSignOut';
import { useMembership, getRoleLabel } from '@/hooks/useMembership';
import { getNavigationForRole } from '@/lib/navigation';
import { usePendingEditRequests } from '@/hooks/useEditRequestCounts';
import { FarmSwitcher } from './FarmSwitcher';
import { NavCountBadge } from './NavCountBadge';

export function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { signOut, signingOut } = useSignOut();
  const { role } = useMembership();

  const navigation = getNavigationForRole(role);
  const pendingEditRequests = usePendingEditRequests();

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-5 pb-4">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center">
        <Link to="/overview" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
            <Waves className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-gray-900">HiponHero</span>
        </Link>
      </div>

      <FarmSwitcher variant="sidebar" />

      {/* Navigation */}
      <nav className="flex flex-1 flex-col" data-tour="sidebar-ponds">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                // Add data-tour attribute for specific items
                const tourAttr = item.href === '/edit-requests' ? { 'data-tour': 'edit-requests' } : {};
                const pending = item.href === '/edit-requests' ? pendingEditRequests : 0;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-lg p-2.5 text-sm font-medium leading-6 transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      )}
                      aria-label={pending > 0 ? `${item.name}, ${pending} pending` : undefined}
                      {...tourAttr}
                    >
                      <item.icon
                        className={cn(
                          'h-5 w-5 shrink-0 transition-colors',
                          isActive
                            ? 'text-primary-600'
                            : 'text-gray-400 group-hover:text-gray-600'
                        )}
                      />
                      {item.name}
                      <NavCountBadge count={pending} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* User section */}
          <li className="mt-auto">
            <div className="rounded-lg p-2.5 bg-gray-50">
              <div className="flex items-center gap-x-3">
                <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <span className="text-primary-700 text-sm font-semibold">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.email || 'User'}
                  </p>
                  {role && (
                    <p className="text-xs text-gray-500">{getRoleLabel(role)}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => signOut()}
                disabled={signingOut}
                className="mt-2.5 flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
              >
                {signingOut ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                ) : (
                  <LogOut className="h-4 w-4 text-gray-500" />
                )}
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}
